import { Table, Text } from "@mantine/core";
import { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { ListOrderProps } from "./types";
import { formatCurrency } from "@/utils/common";
import { get } from "lodash";
import { useGetListDomesticBankQuery } from "@/services/transferAPI";
import i18n from "i18n/i18n";

const AMBER = { color: "#F47920", bg: "#FFF3E8", border: "#FCE0C7" };
const GREEN = { color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
const RED = { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
const GRAY = { color: "#525866", bg: "#F4F4F6", border: "#E5E7EB" };
const BLUE = { color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" };

// Nhãn trạng thái thật của giao dịch con
const getActualStatus = (): Record<string, { label: string } & typeof AMBER> => ({
  // Sau khi lô được duyệt, GD con vẫn ở INIT/VALID -> hiển thị "Đang xử lý"
  INIT: { label: i18n.t("APPROVING"), ...AMBER },
  VALID: { label: i18n.t("APPROVING"), ...AMBER },
  PENDING: { label: i18n.t("WAITING_APPROVE"), ...AMBER },
  WAITING_APPROVE: { label: i18n.t("WAITING_APPROVE"), ...AMBER },
  PROCESSING: { label: i18n.t("APPROVING"), ...BLUE },
  APPROVING: { label: i18n.t("APPROVING"), ...BLUE },
  SUCCESS: { label: i18n.t("SUCCESS"), ...GREEN },
  FAIL: { label: i18n.t("FAILED"), ...RED },
  FAILED: { label: i18n.t("FAILED"), ...RED },
  FAIL_REFUNDED: { label: i18n.t("REFUND_ERROR"), ...AMBER },
  REJECTED: { label: i18n.t("REJECT"), ...RED },
  RETURNED: { label: i18n.t("HAS_BEEN_RETURNED"), ...GRAY },
  RETURN: { label: i18n.t("HAS_BEEN_RETURNED"), ...GRAY },
  APPROVED: { label: i18n.t("APPROVED_STATUS"), ...GREEN },
  CANCELLED: { label: i18n.t("CANCELLED"), ...GRAY },
});

const getStatusDisplay = (
  status: string,
  t: any,
  statusVariant: "validity" | "pending" | "actual" = "validity",
  actualMode = ""
) => {
  // Màn kết quả: giao dịch đã gửi đi -> luôn hiển thị "Chờ duyệt" (vàng)
  if (statusVariant === "pending") {
    return { ...AMBER, text: i18n.t("WAITING_APPROVE") };
  }
  // Trạng thái thật của từng giao dịch con
  if (statusVariant === "actual") {
    // Vừa thực hiện hành động trên lô -> mọi GD con hiển thị theo hành động đó
    if (actualMode === "RETURN") return { ...AMBER, text: i18n.t("WAITING_APPROVE") };
    if (actualMode === "APPROVE") return { ...AMBER, text: i18n.t("APPROVING") };
    if (actualMode === "REJECT") return { ...RED, text: i18n.t("REJECTED") };
    // Không có hành động -> trạng thái thật của GD con
    const s = getActualStatus()[(status || "").toUpperCase()];
    return s ? { ...s, text: s.label } : { ...GRAY, text: status || "-" };
  }
  if (status === "INIT" || status === "VALID") {
    return { ...GREEN, text: t("VALID") };
  }
  return { ...RED, text: t("INVALID") };
};

const ListOrder: FC<ListOrderProps> = ({
  orders = [],
  currentPage = 1,
  pageSize = 10,
  showIndex = true,
  showCode = false,
  statusVariant = "validity",
  actualMode,
}) => {
  const { t } = useTranslation();
  const { data: getListDomesticBankData } = useGetListDomesticBankQuery();
  const listDomesticBank = get(
    getListDomesticBankData,
    "body.domesticBankList",
    []
  );
  const getBeneficiaryBank = (bankCode: string): string => {
    const bank = listDomesticBank.find((item) => item.bankCode === bankCode);
    return bank ? bank.bankName : "error";
  };
  return (
    <Table>
      <thead>
        <tr style={{ background: "#F8FAFC" }}>
          {showIndex && <th style={{ textAlign: "center" }}>{t("NOSTT")}</th>}
          {showCode && <th>{t("TRANSACTION_CODE")}</th>}
          <th style={{ minWidth: 150, whiteSpace: "nowrap" }}>
            {t("RECIPIENT_ACCOUNT")}
          </th>
          <th style={{ minWidth: 150 }}>{t("RECIPIENT_NAME")}</th>
          <th style={{ minWidth: 160 }}>{t("BENEFICIARY_BANK")}</th>
          <th style={{ minWidth: 160 }}>{t("DESCRIPTION")}</th>
          <th style={{ minWidth: 120, whiteSpace: "nowrap" }}>{t("AMOUNT")}</th>
          <th>{t("STATUS")}</th>
          <th>{t("ERROR_REASON")}</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((item, index) => {
          const statusDisplay = getStatusDisplay(
            item.status,
            t,
            statusVariant,
            actualMode
          );
          return (
            <tr key={item.batchDetailId}>
              {showIndex && (
                <td style={{ textAlign: "center" }}>
                  {(currentPage - 1) * pageSize + index + 1}
                </td>
              )}
              {showCode && <td>{item.batchDetailId}</td>}
              <td style={{ whiteSpace: "nowrap" }}>
                {item.beneficiaryAccount}
              </td>
              <td>{item.beneficiaryName}</td>
              {/* <td>{getBeneficiaryBank(item.bankCode)}</td> */}
              <td>{item.bankName}</td>
              <td>{item.note}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {formatCurrency(item.amount)}
              </td>
              <td>
                <Text
                  fz={13}
                  fw={600}
                  ta="center"
                  style={{
                    color: statusDisplay.color,
                    backgroundColor: statusDisplay.bg,
                    border: `1px solid ${statusDisplay.border}`,
                    borderRadius: 20,
                    padding: "4px 14px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                  }}
                >
                  {statusDisplay.text}
                </Text>
              </td>
              <td>
                {/* Lý do lỗi/trùng từng dòng (BE validate trả về) — trống nếu hợp lệ */}
                {item.reasonFail && <Text c="red">{item.reasonFail}</Text>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default memo(ListOrder);
